import React, { useCallback, useMemo } from 'react';
import { useExpanded, useTable } from 'react-table';

import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { DownArrow as DownArrowIcon } from '@styled-icons/boxicons-solid/DownArrow';
import { RightArrow as RightArrowIcon } from '@styled-icons/boxicons-solid/RightArrow';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Button,
  HStack,
  Icon,
  IconButton,
} from '@chakra-ui/react';

import Loader from 'components/Loader';

import { Input, State, DefaultPrice, StatePrice } from 'types/input';

import InputsTableRow from './InputsTableRow';
import { Row, SelectedInput } from './types';
import VariationsTable from './VariationsTable';

type Props = {
  data: Input[];
  loading: boolean;
  selectedInputs: SelectedInput[];
  searchFilter: Record<string, string | number>;
  handleSelectInput: (input: Input) => void;
  handleDeleteInput: (input: Input) => void;
};

const InputsTable: React.FC<Props> = ({
  data,
  loading,
  selectedInputs,
  searchFilter,
  handleSelectInput,
  handleDeleteInput,
}) => {
  function isDefaultPrice(price: StatePrice): price is DefaultPrice {
    return 'default' in price;
  }

  const getStatePrice = (input: Input): State => {
    if (!input._source.prices) {
      return {
        default: {
          total: 0,
        },
      } as State;
    }

    const states = Array.from(Object.keys(input._source.prices));
    const state = states[0];

    return input._source.prices[state];
  };

  const getPrice = useCallback((input: Input): number => {
    const price = getStatePrice(input);

    if (isDefaultPrice(price)) {
      return price.default.total;
    }

    if (price.taxed) {
      return price.taxed.total;
    }

    if (price.untaxed) {
      return price.untaxed.total;
    }

    return 0;
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: () => null,
        id: 'expander',
        width: '5%',
        Cell: ({ row }: { row: Row }) => {
          const isExpandable = !!row.original._source.has_children;

          return (
            <HStack justifyContent="center">
              {isExpandable &&
                (row.isExpanded ? (
                  <Icon w={4} h={4} as={DownArrowIcon} />
                ) : (
                  <Icon w={4} h={4} as={RightArrowIcon} />
                ))}
            </HStack>
          );
        },
      },
      {
        Header: 'Código',
        accessor: 'data._source.code',
        width: '10%',
        Cell: ({ row }: { row: Row }) => {
          return row.original._source.code;
        },
      },
      {
        Header: 'Descrição',
        accessor: 'data._source.description',
        width: '70%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <Text
              py={1}
              maxWidth={{ base: '100%', md: '650px' }}
              whiteSpace="pre-wrap"
            >
              {row.original._source.description}
            </Text>
          );
        },
      },
      {
        Header: 'Unidade',
        accessor: 'data._source.unit_measure.description',
        width: '10%',
        Cell: ({ row }: { row: Row }) => {
          return row.original._source.unit_measure.description;
        },
      },
      {
        Header: 'Valor unitário',
        accessor: 'data._source.prices',
        isNumeric: true,
        width: '10%',
        Cell: ({ row }: { row: Row }) => {
          return Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(getPrice(row.original));
        },
      },
      {
        Header: 'Ações',
        accessor: 'actions',
        isNumeric: true,
        width: '15%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <HStack spacing={2}>
              <Button
                colorScheme="green"
                leftIcon={<Icon as={CheckIcon} />}
                onClick={() => handleSelectInput(row.original)}
                disabled={selectedInputs.some(
                  (c) => c._id === row.original._id,
                )}
              >
                {selectedInputs.some((c) => c._id === row.original._id)
                  ? 'Selecionado'
                  : 'Selecionar'}
              </Button>

              {selectedInputs.some((c) => c._id === row.original._id) && (
                <IconButton
                  aria-label="Remover inputs"
                  colorScheme="red"
                  icon={<Icon as={CloseIcon} />}
                  onClick={() => handleDeleteInput(row.original)}
                />
              )}
            </HStack>
          );
        },
      },
    ],
    [getPrice, selectedInputs, handleSelectInput, handleDeleteInput],
  );

  const getRowId = useCallback((row) => {
    return row._id;
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      // @ts-expect-error types
      columns,
      data,
      getRowId,
    },
    useExpanded,
  );

  const renderRowSubComponent = useCallback(
    ({ parentId }) => (
      <VariationsTable
        parentId={parentId}
        searchFilter={searchFilter}
        selectedInputs={selectedInputs}
        handleSelectInput={handleSelectInput}
        handleDeleteInput={handleDeleteInput}
      />
    ),
    [selectedInputs, searchFilter, handleSelectInput, handleDeleteInput],
  );

  return (
    <TableContainer width="100%">
      <Table {...getTableProps()} borderBottom="none" variant="simple">
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps()}
                  width={column.width}
                  // @ts-expect-error isNumeric does not exists on lib type
                  isNumeric={column.isNumeric}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        <Tbody {...getTableBodyProps()}>
          {!loading &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);

              return (
                <InputsTableRow
                  row={row as unknown as Row}
                  visibleColumns={visibleColumns}
                  renderRowSubComponent={renderRowSubComponent}
                  {...row.getRowProps()}
                />
              );
            })}

          {loading && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
              >
                <Loader />
              </Td>
            </Tr>
          )}

          {!loading && data.length === 0 && (
            <Tr>
              <Td
                colSpan={visibleColumns.length}
                borderBottom="none"
                background="gray.100"
                className="text-center"
              >
                Nenhum resultado encontrado.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default InputsTable;
